import React, { useState, useContext } from "react";
import { Box, Typography, Button, IconButton } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";

import { size } from "lodash";
import Loading from "../components/Loading";
import { DataContext } from "../context/DataContext";
import { simpleMessage } from "../helpers/Helpers";
import { apiLogin } from "../api/Account";
import { getAppTheme, getTokens } from "../theme";

// Login split-screen — ver design_handoff_crediapp_redesign/Login CrediApp.dc.html.
// Panel izquierdo de marca con degradado verde->navy y circulos radiales
// decorativos; panel derecho con el formulario. El tema (claro/oscuro) se
// alterna localmente porque en esta pantalla aun no hay sesion que persista
// la preferencia del usuario en el backend.
const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [userName, setUserName] = useState("");
  const [pass, setPass] = useState("");
  const [localDark, setLocalDark] = useState(false);

  const {
    setIsLoading,
    setIsDarkMode,
    setIsLogged,
    setAccess,
    title,
    slogan,
    version,
    icon,
    setIsServerOff,
  } = useContext(DataContext);

  const tema = getAppTheme(localDark);
  const t = getTokens(localDark);

  const validar = () => {
    let isValid = true;
    if (!userName.trim()) {
      isValid = false;
      simpleMessage("Debe ingresar su usuario", "error");
      return;
    }

    if (!pass.trim()) {
      isValid = false;
      simpleMessage("Debe ingresar su contraseña", "error");
      return;
    }

    if (size(pass) < 6) {
      isValid = false;
      simpleMessage("la contraseña es menor de 6 caracteres", "error");
      return;
    }
    return isValid;
  };

  const iniciarSesion = async (event) => {
    event.preventDefault();
    if (!validar()) {
      return;
    }
    setIsLoading(true);
    const data = {
      userName: userName,
      password: pass,
    };
    const result = await apiLogin(data);
    if (!result.statusResponse) {
      simpleMessage(result.error, "error");
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setIsServerOff(result.data.userRol.isOff);
    setIsDarkMode(result.data.isDarkMode);
    setAccess(result.data.userRol.permissions);
    setIsLogged(true);
    setUserName("");
    setPass("");
  };

  const campoSx = {
    display: "flex",
    alignItems: "center",
    gap: "11px",
    backgroundColor: t.field,
    border: `1px solid ${t.fieldLine}`,
    borderRadius: "12px",
    padding: "0 15px",
    height: "50px",
  };

  const inputSx = {
    flex: 1,
    border: "none",
    outline: "none",
    backgroundColor: "transparent",
    fontFamily: "'Lato', sans-serif",
    fontSize: 15,
    color: "text.primary",
    minWidth: 0,
  };

  const labelSx = {
    display: "block",
    fontSize: 13,
    fontWeight: 700,
    color: "text.secondary",
    marginBottom: "8px",
  };

  return (
    <ThemeProvider theme={tema}>
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          width: "100%",
          backgroundColor: "background.default",
          color: "text.primary",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <IconButton
          onClick={() => setLocalDark(!localDark)}
          title="Cambiar tema"
          sx={{
            position: "absolute",
            top: 22,
            right: 22,
            zIndex: 5,
            width: 42,
            height: 42,
            borderRadius: "11px",
            backgroundColor: t.chip,
            border: `1px solid ${t.chipLine}`,
            color: "text.primary",
          }}
        >
          {localDark ? (
            <LightModeRoundedIcon sx={{ fontSize: 19 }} />
          ) : (
            <DarkModeRoundedIcon sx={{ fontSize: 19 }} />
          )}
        </IconButton>

        {/* Panel de marca */}
        <Box
          sx={{
            width: "46%",
            minWidth: 420,
            display: { xs: "none", md: "flex" },
            background: "linear-gradient(160deg,#0a3d2a 0%,#0d1730 62%,#081027 100%)",
            position: "relative",
            overflow: "hidden",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: "48px",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              width: 520,
              height: 520,
              borderRadius: "50%",
              background: "radial-gradient(circle,rgba(18,163,90,.28),transparent 70%)",
              top: -160,
              right: -140,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              width: 360,
              height: 360,
              borderRadius: "50%",
              background: "radial-gradient(circle,rgba(52,199,123,.16),transparent 70%)",
              bottom: -120,
              left: -100,
            }}
          />

          <Box sx={{ display: "flex", alignItems: "center", gap: "13px", position: "relative" }}>
            <Box
              sx={{
                width: 46,
                height: 46,
                borderRadius: "13px",
                backgroundColor: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <img alt="logo" src={icon} style={{ width: 34, height: 34, objectFit: "contain" }} />
            </Box>
            <Box>
              <Typography
                sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, color: "#fff", fontSize: 20, lineHeight: 1 }}
              >
                {title}
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#8fa7c0", marginTop: "4px" }}>{version}</Typography>
            </Box>
          </Box>

          <Box sx={{ position: "relative" }}>
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 700,
                color: "#fff",
                fontSize: 40,
                lineHeight: 1.15,
                letterSpacing: "-.01em",
              }}
            >
              Bienvenido de nuevo<Box component="span" sx={{ color: "#34c77b" }}>.</Box>
            </Typography>
            <Typography sx={{ fontSize: 16, color: "#b9c6d8", marginTop: "18px", maxWidth: 400, lineHeight: 1.6 }}>
              {slogan}
            </Typography>
          </Box>

          <Typography sx={{ position: "relative", fontSize: 12.5, color: "#7c8ba3" }}>
            © {title} {new Date().getFullYear()} · Todos los derechos reservados
          </Typography>
        </Box>

        {/* Formulario */}
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px" }}>
          <Box component="form" onSubmit={iniciarSesion} sx={{ width: "100%", maxWidth: 400 }}>
            <Typography
              sx={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                fontSize: 27,
                color: "text.primary",
                letterSpacing: "-.01em",
              }}
            >
              Iniciar sesión
            </Typography>
            <Typography sx={{ fontSize: 14.5, color: "text.disabled", marginTop: "8px" }}>
              Ingresa tus credenciales para acceder al sistema.
            </Typography>

            <Box sx={{ marginTop: "34px" }}>
              <Typography component="label" sx={labelSx}>
                Usuario
              </Typography>
              <Box sx={campoSx}>
                <PersonOutlineRoundedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                <Box
                  component="input"
                  type="text"
                  autoComplete="username"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Ingrese su usuario"
                  sx={inputSx}
                />
              </Box>
            </Box>

            <Box sx={{ marginTop: "20px" }}>
              <Typography component="label" sx={labelSx}>
                Contraseña
              </Typography>
              <Box sx={campoSx}>
                <LockOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                <Box
                  component="input"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={pass}
                  onChange={(e) => setPass(e.target.value)}
                  placeholder="Ingrese su contraseña"
                  sx={inputSx}
                />
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label="Mostrar u ocultar contraseña"
                  sx={{ padding: 0, color: "text.disabled" }}
                >
                  {showPassword ? (
                    <VisibilityRoundedIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <VisibilityOffRoundedIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </Box>
            </Box>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{
                width: "100%",
                height: 52,
                marginTop: "26px",
                borderRadius: "12px",
                fontSize: 15.5,
              }}
            >
              Iniciar sesión
            </Button>

            <Typography sx={{ textAlign: "center", fontSize: 12.5, color: "text.disabled", marginTop: "28px" }}>
              Copyright © {title} {new Date().getFullYear()} · {version}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Loading />
    </ThemeProvider>
  );
};

export default Login;
